// Deterministic plant care-profile model, ported from the design handoff logic
// class (reference/Cleverplant.dc.html). Given a family's parameters it produces
// the per-day target/optimal bands for moisture, temperature, light and nutrition
// across the 365-day year. Pure functions; no UI state.

import { C, METRICS } from './plantConstants';

// Half-to-even (banker's) rounding, as in the ported model.
export function rnd(x) {
	const f = Math.floor(x);
	const d = x - f;
	if (Math.abs(d - 0.5) < 1e-9) return f % 2 === 0 ? f : f + 1;
	return Math.round(x);
}

export function seasonMonth(name, hemi) {
	const m = C.north_season[name];
	return hemi === 'south' ? ((m + 5) % 12) + 1 : m;
}

export function flip(mo, hemi) {
	return hemi === 'south' ? ((mo + 5) % 12) + 1 : mo;
}

export function circDist(a, b) {
	const d = Math.abs(a - b) % 12;
	return Math.min(d, 12 - d);
}

// activity: seasonal growth multiplier (0..1) for month `mo` given numeric peaks.
export function activity(mo, peaks, baseline, hemi) {
	let a = baseline;
	for (const p of peaks) {
		const c = flip(p[0], hemi);
		a += p[1] * Math.exp(-(circDist(mo, c) ** 2) / (2 * p[2] * p[2]));
	}
	return Math.min(a, 1);
}

export function ambientTemp(mo, hemi) {
	const s = seasonMonth('summer', hemi);
	return (Math.cos(((mo - s) / 12) * 2 * Math.PI) + 1) / 2;
}

// Convert a class's default season-named peaks to numeric [month, height, width].
export function classPeaksNumeric(cls) {
	return C.classes[cls].peaks.map((p) => [typeof p[0] === 'string' ? C.north_season[p[0]] : p[0], p[1], p[2]]);
}

function modulation(metric, cls, hemi, peaks) {
	const co = C.classes[cls];
	const out = [];
	for (let m = 1; m <= 12; m++) {
		if (metric === 'temperature') out.push(-co.temp_amp * (1 - ambientTemp(m, hemi)));
		else out.push(activity(m, peaks, co.baseline, hemi));
	}
	return out;
}

// buildRows: 12 monthly anchors for a metric. `level` is a 4-tuple
// [target_min, optimal_min, optimal_max, target_max].
export function buildRows(metric, level, cls, hemi, peaks) {
	const [tmin, omin, omax, tmax] = level;
	const mod = modulation(metric, cls, hemi, peaks);
	const rows = [];
	for (let i = 0; i < 12; i++) {
		let f;
		if (metric === 'temperature') {
			const off = mod[i];
			f = (v) => rnd(v + off);
		} else {
			const fl = Math.max(rnd(tmin * 0.4), 1);
			const mm = mod[i];
			f = (v) => rnd(fl + (v - fl) * mm);
		}
		rows.push({ month: i + 1, doy: C.month_doy[i], target_min: f(tmin), optimal_min: f(omin), optimal_max: f(omax), target_max: f(tmax) });
	}
	return rows;
}

// interp: circular linear interpolation between the 12 monthly anchors.
export function interp(rows, doy) {
	const s = [...rows].sort((a, b) => a.doy - b.doy);
	const pts = [{ ...s[s.length - 1], doy: s[s.length - 1].doy - 365 }, ...s, { ...s[0], doy: s[0].doy + 365 }];
	let lo = pts[0];
	let hi = pts[pts.length - 1];
	for (let i = 0; i < pts.length - 1; i++) {
		if (doy >= pts[i].doy && doy <= pts[i + 1].doy) {
			lo = pts[i];
			hi = pts[i + 1];
			break;
		}
	}
	const sp = hi.doy - lo.doy;
	const t = sp === 0 ? 0 : (doy - lo.doy) / sp;
	const b = {};
	for (const k of ['target_min', 'optimal_min', 'optimal_max', 'target_max']) b[k] = lo[k] + (hi[k] - lo[k]) * t;
	b.center = (b.optimal_min + b.optimal_max) / 2;
	return b;
}

export function status(b, v) {
	if (v >= b.optimal_min && v <= b.optimal_max) return 'optimal';
	if (v >= b.target_min && v <= b.target_max) return 'tolerated';
	return 'out';
}

// Ordered level names, low -> high.
export function lvlAsc(metric) {
	return Object.keys(C.base_bands[metric]).slice().reverse();
}

export function baseIdxOf(metric, name) {
	const i = lvlAsc(metric).indexOf(name);
	return i < 0 ? 0 : i;
}

// baseBandFor: the 4-tuple base band at a continuous (fractional) level index,
// linearly interpolating between adjacent named levels.
export function baseBandFor(metric, frac) {
	const asc = lvlAsc(metric);
	const f = Math.max(0, Math.min(asc.length - 1, frac));
	const i0 = Math.floor(f);
	const i1 = Math.min(i0 + 1, asc.length - 1);
	const t = f - i0;
	const a = C.base_bands[metric][asc[i0]];
	const b = C.base_bands[metric][asc[i1]];
	return a.map((v, k) => v + (b[k] - v) * t);
}

// baseLabel: nearest named level, or a blended "A–B" label between two.
export function baseLabel(metric, frac) {
	const asc = lvlAsc(metric);
	const f = Math.max(0, Math.min(asc.length - 1, frac));
	const i0 = Math.floor(f);
	const i1 = Math.min(i0 + 1, asc.length - 1);
	const t = f - i0;
	const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
	if (i0 === i1 || t < 0.06) return cap(asc[i0]);
	if (t > 0.94) return cap(asc[i1]);
	return `${cap(asc[i0])}–${cap(asc[i1])}`;
}

// scaleRows: view-only tolerance scaling applied after the model.
export function scaleRows(rows, bandScale) {
	const opt = bandScale.opt;
	const tol = bandScale.tol;
	if (opt === 1 && tol === 1) return rows;
	return rows.map((r) => {
		const c = (r.optimal_min + r.optimal_max) / 2;
		const oMin = c - (c - r.optimal_min) * opt;
		const oMax = c + (r.optimal_max - c) * opt;
		return {
			...r,
			optimal_min: oMin,
			optimal_max: oMax,
			target_min: oMin - (r.optimal_min - r.target_min) * tol,
			target_max: oMax + (r.target_max - r.optimal_max) * tol,
		};
	});
}

// rowsFor: the 12 monthly anchors for a metric from a family's parameters.
export function rowsFor(metric, params) {
	const level = baseBandFor(metric, params.base_levels[metric]);
	const rows = buildRows(metric, level, params.phenology_class, params.hemisphere, params.peaks);
	return scaleRows(rows, params.band_scale);
}

// dailySeries: per-day band values for a metric across the year, for charting.
// Returns { days, target_min, optimal_min, optimal_max, target_max, center }.
export function dailySeries(metric, params) {
	const rows = rowsFor(metric, params);
	const out = { days: [], target_min: [], optimal_min: [], optimal_max: [], target_max: [], center: [] };
	for (let doy = 1; doy <= 365; doy++) {
		const b = interp(rows, doy);
		out.days.push(doy);
		out.target_min.push(b.target_min);
		out.optimal_min.push(b.optimal_min);
		out.optimal_max.push(b.optimal_max);
		out.target_max.push(b.target_max);
		out.center.push(b.center);
	}
	return out;
}

// localizeParams reshapes params with a location overlay's family-agnostic
// scales (mirrors the backend profile.Localize). Returns params unchanged when
// no overlay is given.
export function localizeParams(params, overlay) {
	if (!overlay) return params;
	const out = { ...params, peaks: params.peaks.map((p) => [...p]) };
	if (overlay.peak_height_scale != null || overlay.peak_width_scale != null) {
		out.peaks = out.peaks.map(([c, h, w]) => [
			c,
			overlay.peak_height_scale != null ? Math.max(0, Math.min(1, h * overlay.peak_height_scale)) : h,
			overlay.peak_width_scale != null ? w * overlay.peak_width_scale : w,
		]);
	}
	return out;
}

// tempAmplitude: peak-to-trough °C of the temperature center line, for the readout.
export function tempAmplitude(params) {
	const rows = rowsFor('temperature', params);
	const centers = rows.map((r) => (r.optimal_min + r.optimal_max) / 2);
	return Math.max(...centers) - Math.min(...centers);
}

export { METRICS };
